import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { MCard, MSpinner, MCheckboxGroup } from "components/Shared";
import CustomizeRecommendationsCard from "./CustomizeRecommendationsCard.tsx";
import { getEnabledFeatures, getCaseInfo } from "actions/customer";
import { getCaseRecommendations } from "requests/homeowner/ho";
import { addCustomerAction, markReviewed } from "actions/underwriterActions";
import { updateCaseHeader, getCaseForms } from "actions/homeowner/caseforms";
import Grid from "@mui/material/Grid";
import {
  getCoverageAFields,
  getHazardSummaryFields,
  getCustomerCommentsFields,
  getCustomerActionsHistoryFields,
} from "typeConstants/mCardDefinitions/customer";
import { dateTimeToString } from "components/Shared/Formatters";
import { UNDERWRITING_MARK_REVIEWED_REQUIRED } from "typeConstants/customerFeatures";
import { DialogContext } from "components/Shared/DialogProvider.tsx";
import { updateRecommendationInclusions } from "requests/recommendations/index.ts";
import responseHandler from "responseHandler";
import { userHasRight } from "selectors/rights";
import { INCLUDE_REC_IN_LETTER_UPDATE } from "typeConstants/rights";
import { PhoenixFormsContext } from "components/PhoenixFormsV4/PhoenixFormsProvider.tsx";
class ReviewCase extends Component {
  static contextType = DialogContext;

  constructor(props) {
    super(props);
    this.getControlNumber = this.getControlNumber.bind(this);
    this.getActionRequest = this.getActionRequest.bind(this);
    this.resetFields = this.resetFields.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleMarkReviewed = this.handleMarkReviewed.bind(this);
    this.customerReviewActionsRef = React.createRef();
    this.customerCommentsRef = React.createRef();
    this.coverageAEndorsedRef = React.createRef();
    this.mountedRef = { current: true };
    this.state = {
      selectedCustomerReviewActions: null,
      coverageAEndorsed: null,
      customerComments: null,
      recommendations: null,
      fetchingRecommendations: true,
    };
    this.handleRecommendationsChange = this.handleRecommendationsChange.bind(this);
    // Need to reload phoenix forms if we save the recs, or else you get stale data if you go back to the forms
    this.reloadPnxForms = null;
  }

  componentDidMount() {
    const controlNumber = this.getControlNumber();
    if (controlNumber) {
      this.props.getCaseInfo(controlNumber);
      getCaseRecommendations(
        controlNumber,
        (recs) => {
          this.setState({ recommendations: recs, fetchingRecommendations: false });
        },
        this.mountedRef,
        true,
      );
    }
    // I think it's safe to assume that if we already have the list of forms loaded for this case that we don't have to re-load it, we just have to check if phoenix recs exists
    if (!this.props.caseFormsList || this.props.caseFormsList.length === 0) {
      this.props.getCaseForms(controlNumber);
    }
  }

  componentDidUpdate() {
    const { caseInfo } = this.props;
    if (caseInfo) {
      const { coverageAEndorsed } = caseInfo;
      if (!this.state.coverageAEndorsed && coverageAEndorsed) {
        this.setState({ coverageAEndorsed });
      }
      if (caseInfo.customerNumber && !this.props.fetchingEnabledFeatures && !this.props.enabledFeatures) {
        this.props.getEnabledFeatures(this.props.caseInfo.customerNumber, () => {});
      }

      this.selectedUnderwriterActions = this.props.caseInfo?.selectedUnderwriterActions;
    }
  }

  componentWillUnmount() {
    this.mountedRef.current = false;
  }

  getControlNumber() {
    const { match, controlNumber } = this.props;
    return (match && match.params && match.params.controlNumber) || controlNumber;
  }

  getActionRequest() {
    return {
      controlNumber: this.getControlNumber(),
      caseActions: this.getSelectedActions(),
      comments: this.customerCommentsRef.current.value,
    };
  }

  getSelectedActions() {
    const {
      caseInfo: { availableUnderwriterActions },
    } = this.props;
    const selectedCodes = this.customerReviewActionsRef.current.getChecked();
    return availableUnderwriterActions.filter((item) => {
      return selectedCodes.includes(item.code);
    });
  }

  resetFields() {
    this.customerCommentsRef.current.value = "";
  }

  saveRecs = () => {
    if (this.state.recommendations && this.state.recommendations.length > 0) {
      updateRecommendationInclusions(
        this.getControlNumber(),
        { requests: this.state.recommendations },
        this.mountedRef,
        () => {
          this.props.makeSuccessToast("Recommendation inclusions updated successfully!");
          this.reloadPnxForms?.();
        },
        () => {
          this.props.makeErrorToast("Failed to update recommendation inclusions, please try again.");
        },
      );
    }
  };

  handleSave() {
    const selectedActions = this.getSelectedActions();
    this.saveRecs();
    // If this feature is enabled, the case must be marked reviewed to take an underwriter action
    if (
      selectedActions.length > 0 &&
      this.props.enabledFeatures?.map((s) => s.feature).includes(UNDERWRITING_MARK_REVIEWED_REQUIRED) &&
      !this.props.caseInfo?.dateReviewed
    ) {
      this.context.showYesNo(
        null,
        "Case must be marked reviewed when entering initial reviewer action. Should the case be marked as reviewed?",
        this.handleMarkReviewed,
        () => {},
        "Confirm",
        "Cancel",
      );
    } else {
      this.props.addCustomerAction(this.getActionRequest());
      this.resetFields();
      this.props.updateCaseHeader(this.getControlNumber(), { coverageAEndorsed: this.state.coverageAEndorsed });
      this.selectedUnderwriterActions = this.props.caseInfo.availableUnderwriterActions.filter((action) =>
        selectedActions.some((s) => s.code === action.code),
      );
    }
  }

  handleMarkReviewed() {
    this.saveRecs();
    this.props.markReviewed(this.getActionRequest());
    this.resetFields();
    this.props.updateCaseHeader(this.getControlNumber(), { coverageAEndorsed: this.state.coverageAEndorsed });
    const selectedActions = this.getSelectedActions();
    this.selectedUnderwriterActions = this.props.caseInfo.availableUnderwriterActions.filter((action) =>
      selectedActions.some((s) => s.code === action.code),
    );
  }

  handleRecommendationsChange(recommendations) {
    this.setState({ recommendations });
  }

  render() {
    if (!this.props.caseInfo || this.props.fetchingEnabledFeatures) return <MSpinner />;
    const {
      caseInfo: {
        availableUnderwriterActions,
        hazards,
        coverageAIn,
        coverageAOut,
        coverageAEndorsed,
        underwriterActionsAudits,
        dateReviewed,
        reviewedBy,
      },
    } = this.props;
    const uwActionCheckboxes = availableUnderwriterActions
      .filter((a) => a.isUserSelectable)
      .map(({ name, code }) => ({
        value: code,
        label: name,
        defaultChecked:
          (this.selectedUnderwriterActions ?? this.props.caseInfo?.selectedUnderwriterActions)?.some(
            (action) => action.code === code,
          ) ?? false,
      }));
    const canCustomizeRecs =
      this.props.caseFormsList?.some((form) => form.formCode === "PHOENIX_RECOMMENDATIONS_V1") &&
      this.props.canCustomizeRecs;
    return (
      <>
        <PhoenixFormsContext.Consumer>
          {(context) => {
            this.reloadPnxForms = context.reload;
          }}
        </PhoenixFormsContext.Consumer>
        <Grid container spacing={2}>
          <Grid size={12}>
            <MCard
              title={"Reviewer Action Taken"}
              fields={[
                {
                  key: "reviewer-actions-taken",
                  control: (
                    <MCheckboxGroup
                      controlMethods={(cm) => (this.customerReviewActionsRef.current = cm)}
                      fields={uwActionCheckboxes}
                      cellSize={4}
                    />
                  ),
                },
              ]}
              disableHoverStyles
            />
          </Grid>
          {canCustomizeRecs && (
            <Grid size={12}>
              <CustomizeRecommendationsCard
                recommendations={this.state.recommendations}
                onRecommendationsChange={this.handleRecommendationsChange}
                isFetching={this.state.fetchingRecommendations}
              />
            </Grid>
          )}
          <Grid size={12}>
            <MCard
              title={"Replacement Cost & Hazards"}
              fields={[
                getCoverageAFields({ coverageAIn, coverageAOut, coverageAEndorsed }, true),
                getHazardSummaryFields(hazards, true),
              ]}
              onEditFinished={({ value }) => this.setState({ coverageAEndorsed: value })}
              onlyEditable
            />
          </Grid>
          <Grid size={12}>
            <MCard
              title={"Review Comments"}
              fields={getCustomerCommentsFields({ innerRef: this.customerCommentsRef })}
            />
          </Grid>
          <Grid size={12}>
            <MCard
              title={"Actions History"}
              fields={getCustomerActionsHistoryFields(underwriterActionsAudits)}
              disableHoverStyles
            />
          </Grid>
          <Grid size={12}>
            <Button
              onClick={this.handleSave}
              disabled={this.state.fetchingRecommendations}
            >
              Save
            </Button>
            {!dateReviewed && (
              <Button
                sx={{ ml: 1 }}
                onClick={this.handleMarkReviewed}
                disabled={this.state.fetchingRecommendations}
              >
                Mark Reviewed
              </Button>
            )}
            {dateReviewed && <Box component="span" sx={{ ml: 2 }}>{`Reviewed by ${reviewedBy} on ${dateTimeToString(dateReviewed)}`}</Box>}
          </Grid>
        </Grid>
      </>
    );
  }
}

ReviewCase.propTypes = {
  // Pure Props
  controlNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  // Router Props
  match: PropTypes.shape({
    params: PropTypes.shape({
      controlNumber: PropTypes.string.isRequired,
    }),
  }),

  // Redux State Props
  caseInfo: PropTypes.object,
  enabledFeatures: PropTypes.array,
  fetchingEnabledFeatures: PropTypes.bool,
  canCustomizeRecs: PropTypes.bool,
  caseFormsList: PropTypes.array,

  //Redux Action Props
  getCaseInfo: PropTypes.func.isRequired,
  addCustomerAction: PropTypes.func.isRequired,
  updateCaseHeader: PropTypes.func.isRequired,
  updateHeader: PropTypes.func.isRequired,
  useDispatch: PropTypes.func.isRequired,
  markReviewed: PropTypes.func.isRequired,
  getEnabledFeatures: PropTypes.func,
  makeSuccessToast: PropTypes.func,
  makeErrorToast: PropTypes.func,
  getCaseForms: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  caseInfo: state.homeownerCustomer.caseInfo,
  enabledFeatures: state.customer.enabledFeatures,
  fetchingEnabledFeatures: state.customer.fetchingEnabledFeatures,
  canCustomizeRecs: userHasRight(INCLUDE_REC_IN_LETTER_UPDATE)(state),
  caseFormsList: state.caseForms.caseFormsList,
});

const mapDispatchToProps = (dispatch) => ({
  getCaseInfo(controlNumber) {
    dispatch(getCaseInfo(controlNumber));
  },
  addCustomerAction(action) {
    dispatch(addCustomerAction(action));
  },
  updateCaseHeader(controlNumber, fields) {
    dispatch(updateCaseHeader(controlNumber, fields));
  },
  markReviewed(action) {
    dispatch(markReviewed(action));
  },
  getEnabledFeatures(customerNumber, callback) {
    dispatch(getEnabledFeatures(customerNumber, callback));
  },
  makeSuccessToast(message) {
    responseHandler.customToast(message, "success", dispatch);
  },
  makeErrorToast(message) {
    responseHandler.customToast(message, "error", dispatch);
  },
  getCaseForms(controlNumber) {
    dispatch(getCaseForms(controlNumber));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ReviewCase);
